import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { useUploadGuard } from '@/components/UploadGuard';
import { Zap, Link2, CheckCircle, XCircle, Send, RefreshCw, Trash2, ExternalLink, Copy, TestTube, AlertTriangle, Activity, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

function MetaLeadsConfig({ user, onLogout }) {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [showLogs, setShowLogs] = React.useState(false);
  const [showTestPanel, setShowTestPanel] = React.useState(false);
  const [diagResults, setDiagResults] = React.useState(null);
  const [companies, setCompanies] = React.useState([]);
  const [webhookHealth, setWebhookHealth] = React.useState(null);
  const { ensureCompanyId, guardModal } = useUploadGuard(user);

  // Form state
  const [formData, setFormData] = React.useState({
    page_access_token: '',
    verify_token: '',
    company_id: '',
    page_id: '',
    default_brand: '',
    default_model: '',
    default_assigned_to: '',
    skip_validation: false,
  });

  // Facebook Pages fetching
  const [userToken, setUserToken] = React.useState('');
  const [fetchingPages, setFetchingPages] = React.useState(false);
  const [availablePages, setAvailablePages] = React.useState([]);

  // Test/Simulate lead form
  const [testData, setTestData] = React.useState({
    first_name: 'Test Meta Lead',
    last_name: '',
    mobile: '',
    email: '',
    brand: '',
    model: '',
  });

  // Sync missed leads
  const [syncFormId, setSyncFormId] = React.useState('');
  const [syncing, setSyncing] = React.useState(false);
  const [syncResult, setSyncResult] = React.useState(null);
  const [showSyncPanel, setShowSyncPanel] = React.useState(false);
  const [uploadingCsv, setUploadingCsv] = React.useState(false);

  React.useEffect(() => {
    fetchConfig();
    fetchCompanies();
    fetchWebhookHealth();
    // Auto-fill company_id for company admins
    if (user?.company_id) {
      setFormData(prev => ({ ...prev, company_id: prev.company_id || user.company_id }));
    }
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/meta-leads/config`, getAuthHeader());
      setConfig(response.data);
      if (response.data.configured) {
        setFormData(prev => ({
          ...prev,
          company_id: response.data.company_id || '',
          page_id: response.data.page_id || '',
          default_brand: response.data.default_brand || '',
          default_model: response.data.default_model || '',
          default_assigned_to: response.data.default_assigned_to || '',
        }));
      }
    } catch {
      // Not configured yet
      setConfig({ configured: false });
    }
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies`, getAuthHeader());
      setCompanies(response.data.companies || response.data || []);
    } catch { /* ignore */ }
  };

  const fetchWebhookHealth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/meta-leads/webhook-health`, getAuthHeader());
      setWebhookHealth(response.data);
    } catch { /* ignore */ }
  };

  const handleSimulateWebhook = async () => {
    if (!testData.mobile) {
      toast.error('Mobile number is required');
      return;
    }
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/simulate-webhook`, testData, getAuthHeader());
      if (response.data.status === 'created') {
        toast.success(response.data.message);
        fetchWebhookHealth();
      } else if (response.data.status === 'duplicate') {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Simulation failed');
    }
  };

  const handleSyncLeads = async () => {
    if (!syncFormId.trim()) {
      toast.error('Please enter a Form ID');
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/sync-leads`, { form_id: syncFormId.trim() }, getAuthHeader());
      setSyncResult(response.data);
      if (response.data.synced > 0) {
        toast.success(`Synced ${response.data.synced} new leads!`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); e.target.value = ''; return; }
    setUploadingCsv(true);
    setSyncResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('target_company_id', targetCo);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/upload-csv`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSyncResult(response.data);
      if (response.data.synced > 0) {
        toast.success(`Imported ${response.data.synced} leads from CSV!`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV upload failed');
    } finally {
      setUploadingCsv(false);
      e.target.value = '';
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/meta-leads/webhook-logs`, getAuthHeader());
      setLogs(response.data.logs || []);
      setShowLogs(true);
    } catch {
      toast.error('Failed to fetch logs');
    }
  };

  const handleSave = async () => {
    if (!formData.page_access_token || !formData.verify_token || !formData.company_id) {
      toast.error('Page Access Token, Verify Token, and Company are required');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/configure`, formData, getAuthHeader());
      toast.success(response.data.message);
      if (response.data.page_subscription === 'subscribed') {
        toast.success('Page subscribed to leadgen webhooks!');
      } else if (response.data.page_subscription && response.data.page_subscription.startsWith('failed')) {
        toast.warning('Config saved but page subscription failed. Click "Subscribe Page" to retry.');
      }
      fetchConfig();
      // Auto-subscribe after save
      try {
        const subRes = await axios.post(`${BACKEND_URL}/api/meta-leads/subscribe-page`, {}, getAuthHeader());
        if (subRes.data.status === 'subscribed') {
          toast.success('Page auto-subscribed to webhooks!');
        }
      } catch { /* subscription will be retried manually */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Configuration failed');
    }
    setSaving(false);
  };

  const handleSubscribePage = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/subscribe-page`, {}, getAuthHeader());
      if (response.data.status === 'subscribed') {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || 'Subscription failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe page');
    }
  };

  const handleFetchPages = async () => {
    if (!userToken) {
      toast.error('Please paste your User Access Token from Graph API Explorer first');
      return;
    }
    setFetchingPages(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/fetch-pages`, { user_access_token: userToken }, getAuthHeader());
      if (response.data.status === 'success' && response.data.pages.length > 0) {
        setAvailablePages(response.data.pages);
        toast.success(`Found ${response.data.pages.length} page(s)! Select one below.`);
      } else {
        toast.error(response.data.message || 'No pages found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch pages');
    }
    setFetchingPages(false);
  };

  const handleSelectPage = (page) => {
    setFormData(prev => ({
      ...prev,
      page_id: page.id,
      page_access_token: page.access_token,
    }));
    setAvailablePages([]);
    toast.success(`Selected: ${page.name} (ID: ${page.id})`);
  };

  const handleDiagnose = async () => {
    setDiagResults(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/diagnose`, {}, getAuthHeader());
      setDiagResults(response.data);
      if (response.data.checks.some(c => c.status === 'fail')) {
        toast.error('Some checks failed — see diagnosis results below');
      } else {
        toast.success('All checks passed!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Diagnosis failed');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disable Meta Lead Ads integration? Existing leads will NOT be deleted.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/meta-leads/disconnect`, {}, getAuthHeader());
      toast.success('Meta Lead Ads disabled');
      setConfig({ configured: false });
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const handleTestLead = async () => {
    if (!testData.mobile) {
      toast.error('Mobile number is required for test');
      return;
    }
    try {
      const response = await axios.post(`${BACKEND_URL}/api/meta-leads/test`, testData, getAuthHeader());
      if (response.data.status === 'created') {
        toast.success(`Test lead created: #${response.data.lead_no}`);
      } else if (response.data.status === 'duplicate') {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const webhookUrl = `${BACKEND_URL}/api/webhooks/meta-leads`;

  if (loading) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title="Meta Lead Ads">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
        </div>
      </LeadsLayout>
    );
  }

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Meta Lead Ads Integration">
      {guardModal}
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Meta Lead Ads Integration</h1>
                <p className="text-white/80 text-sm">Auto-capture leads from Facebook & Instagram ad campaigns</p>
              </div>
              {config?.configured && (
                <div className="ml-auto flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                  <span className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-white text-sm font-medium">Active</span>
                </div>
              )}
            </div>
          </div>

          {/* Status / Config Form */}
          <div className="p-6">
            {config?.configured ? (
              /* Connected View */
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-800">Connected Page</h3>
                    </div>
                    <p className="text-green-700 font-medium">{config.page_name || 'Facebook Page'}</p>
                    <p className="text-green-600 text-xs mt-1">Page ID: {config.page_id}</p>
                    {config.configured_at && <p className="text-green-600 text-xs">Since: {new Date(config.configured_at).toLocaleDateString()}</p>}
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-800">Webhook URL</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded break-all flex-1">{webhookUrl}</code>
                      <button onClick={() => copyToClipboard(webhookUrl)} className="p-1.5 hover:bg-blue-200 rounded-lg" title="Copy">
                        <Copy className="w-4 h-4 text-blue-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Defaults */}
                {(config.default_brand || config.default_model || config.default_assigned_to) && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h3 className="font-semibold text-gray-700 mb-2 text-sm">Default Assignments</h3>
                    <div className="flex gap-4 text-sm">
                      {config.default_brand && <span className="text-gray-600">Brand: <strong>{config.default_brand}</strong></span>}
                      {config.default_model && <span className="text-gray-600">Model: <strong>{config.default_model}</strong></span>}
                      {config.default_assigned_to && <span className="text-gray-600">Sales Rep: <strong>{config.default_assigned_to}</strong></span>}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button onClick={handleSubscribePage} className="px-4 py-2 bg-green-100 text-green-700 rounded-xl text-sm font-medium hover:bg-green-200 flex items-center gap-2" data-testid="subscribe-page-btn">
                    <Zap className="w-4 h-4" /> Subscribe Page to Webhooks
                  </button>
                  <button onClick={() => setShowSyncPanel(!showSyncPanel)} className="px-4 py-2 bg-teal-100 text-teal-700 rounded-xl text-sm font-medium hover:bg-teal-200 flex items-center gap-2" data-testid="sync-leads-btn">
                    <RefreshCw className="w-4 h-4" /> Sync Missed Leads
                  </button>
                  <button onClick={handleDiagnose} className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-xl text-sm font-medium hover:bg-yellow-200 flex items-center gap-2" data-testid="diagnose-btn">
                    <AlertTriangle className="w-4 h-4" /> Diagnose Config
                  </button>
                  <button onClick={() => { fetchLogs(); fetchWebhookHealth(); }} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl text-sm font-medium hover:bg-blue-200 flex items-center gap-2" data-testid="view-logs-btn">
                    <RefreshCw className="w-4 h-4" /> View Webhook Logs
                  </button>
                  <button onClick={() => setShowTestPanel(!showTestPanel)} className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-200 flex items-center gap-2" data-testid="test-lead-btn">
                    <TestTube className="w-4 h-4" /> Test / Simulate Lead
                  </button>
                  <button onClick={() => { setConfig({ configured: false }); }} className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl text-sm font-medium hover:bg-orange-200 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Reconfigure
                  </button>
                  <button onClick={handleDisconnect} className="px-4 py-2 bg-red-100 text-red-700 rounded-xl text-sm font-medium hover:bg-red-200 flex items-center gap-2" data-testid="disconnect-meta-btn">
                    <Trash2 className="w-4 h-4" /> Disconnect
                  </button>
                </div>

                {/* Webhook Health Status */}
                {webhookHealth && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-testid="webhook-health">
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="w-4 h-4 text-gray-600" />
                      <h3 className="font-semibold text-gray-700 text-sm">Webhook Health</h3>
                      <button onClick={fetchWebhookHealth} className="ml-auto text-xs text-blue-600 hover:underline">Refresh</button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                      <div className="bg-white rounded-lg p-2 border">
                        <div className="text-lg font-bold text-gray-800">{webhookHealth.total_webhook_events}</div>
                        <div className="text-xs text-gray-500">Total Events</div>
                      </div>
                      <div className="bg-white rounded-lg p-2 border">
                        <div className="text-lg font-bold text-blue-600">{webhookHealth.total_raw_incoming}</div>
                        <div className="text-xs text-gray-500">FB Payloads</div>
                      </div>
                      <div className="bg-white rounded-lg p-2 border">
                        <div className="text-lg font-bold text-green-600">{webhookHealth.total_leads_created}</div>
                        <div className="text-xs text-gray-500">Leads Created</div>
                      </div>
                      <div className="bg-white rounded-lg p-2 border">
                        <div className="text-xs text-gray-500 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Last Received</div>
                        <div className="text-xs font-medium text-gray-700 mt-1">
                          {webhookHealth.last_raw_incoming ? new Date(webhookHealth.last_raw_incoming.timestamp).toLocaleString() : 'Never'}
                        </div>
                      </div>
                    </div>
                    {webhookHealth.total_raw_incoming === 0 && (
                      <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-700">
                        No real webhook payloads received from Facebook yet. Use <strong>"Test / Simulate Lead"</strong> to verify the pipeline, or click <strong>"Test"</strong> next to "leadgen" in your <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Facebook Developer Console</a> to send a test event.
                      </div>
                    )}
                  </div>
                )}

                {/* Diagnosis Results */}
                {diagResults && (
                  <div className="bg-gray-50 rounded-xl p-4 border space-y-2" data-testid="diag-results">
                    <h3 className="font-semibold text-gray-800 text-sm">Diagnosis Results</h3>
                    {diagResults.checks.map((c, i) => (
                      <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded-lg ${c.status === 'pass' ? 'bg-green-50 text-green-800' : c.status === 'warn' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800'}`}>
                        <span className="font-bold whitespace-nowrap">{c.status === 'pass' ? '✓' : c.status === 'warn' ? '!' : '✗'} {c.check}:</span>
                        <span className="break-all">{c.detail}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Important Notice */}
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <h3 className="font-semibold text-amber-800 text-sm mb-2">Checklist for Live Lead Capture</h3>
                  <ul className="text-amber-700 text-xs space-y-1.5">
                    <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" /> Config saved with correct Page Access Token & Page ID</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" /> <strong>"leadgen"</strong> field subscribed in Facebook Developer Console</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" /> Callback URL in Facebook matches: <code className="bg-amber-100 px-1 rounded text-[10px]">{webhookUrl}</code></li>
                    <li className="flex items-start gap-2">
                      {config?.is_active ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-red-500 shrink-0" />}
                      Click <strong>"Subscribe Page to Webhooks"</strong> after saving config
                    </li>
                    <li className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" /> Facebook App must be in <strong>Published/Live</strong> mode (not Development)</li>
                    <li className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" /> Test using the <strong>"Test"</strong> button in Facebook Console next to "leadgen"</li>
                  </ul>
                </div>
              </div>
            ) : (
              /* Setup Form */
              <div className="space-y-5">
                {/* Setup Guide */}
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <h3 className="font-semibold text-amber-800 mb-2">Setup Guide</h3>
                  <ol className="text-sm text-amber-700 space-y-1.5 list-decimal list-inside">
                    <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Facebook Developers</a> and open your App</li>
                    <li>Under <strong>Products</strong>, add <strong>"Webhooks"</strong></li>
                    <li>Subscribe to <strong>Page</strong> → <strong>leadgen</strong> field</li>
                    <li>Set the <strong>Callback URL</strong> to the webhook URL below</li>
                    <li>Set the <strong>Verify Token</strong> to match what you enter here</li>
                    <li>Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Graph API Explorer</a>, select your App, generate a <strong>User Token</strong> with <code>pages_show_list</code>, <code>leads_retrieval</code>, <code>pages_manage_metadata</code>, <code>pages_manage_ads</code> permissions</li>
                    <li>Paste the User Token below and click <strong>"Fetch My Pages"</strong> to auto-detect your Page ID and Page Token</li>
                    <li><strong className="text-red-700">Critical:</strong> After saving config, click <strong>"Subscribe Page to Webhooks"</strong></li>
                  </ol>
                </div>

                {/* Webhook URL to copy */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <label className="block text-sm font-medium text-blue-800 mb-1">Your Webhook URL (paste this in Facebook)</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-blue-700 bg-blue-100 px-3 py-2 rounded-lg break-all flex-1" data-testid="webhook-url">{webhookUrl}</code>
                    <button onClick={() => copyToClipboard(webhookUrl)} className="p-2 bg-blue-200 hover:bg-blue-300 rounded-lg" title="Copy URL">
                      <Copy className="w-4 h-4 text-blue-700" />
                    </button>
                  </div>
                </div>

                {/* AUTO-FETCH PAGES - Recommended approach */}
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Auto-Detect Page (Recommended)
                  </h3>
                  <p className="text-xs text-green-700 mb-3">
                    Paste your <strong>User Access Token</strong> from <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Graph API Explorer</a>. 
                    Select <strong>"User Token"</strong> and add ALL these permissions: <code className="bg-green-100 px-1 rounded">pages_show_list</code>, <code className="bg-green-100 px-1 rounded">leads_retrieval</code>, <code className="bg-green-100 px-1 rounded">pages_manage_metadata</code>, <code className="bg-green-100 px-1 rounded">pages_manage_ads</code>, <code className="bg-green-100 px-1 rounded">pages_read_engagement</code>.
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="password"
                      value={userToken}
                      onChange={e => setUserToken(e.target.value)}
                      placeholder="Paste User Access Token from Graph API Explorer..."
                      className="flex-1 px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="user-token-input"
                    />
                    <button
                      onClick={handleFetchPages}
                      disabled={fetchingPages || !userToken}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 whitespace-nowrap flex items-center gap-1.5"
                      data-testid="fetch-pages-btn"
                    >
                      {fetchingPages ? <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Fetching...</> : <><RefreshCw className="w-3.5 h-3.5" /> Fetch My Pages</>}
                    </button>
                  </div>

                  {/* Pages List */}
                  {availablePages.length > 0 && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-green-700 font-medium">Select your Facebook Page:</p>
                      {availablePages.map(page => (
                        <button
                          key={page.id}
                          onClick={() => handleSelectPage(page)}
                          className="w-full text-left p-3 bg-white rounded-lg border border-green-300 hover:border-green-500 hover:bg-green-50 transition-colors"
                          data-testid={`select-page-${page.id}`}
                        >
                          <div className="font-medium text-gray-800 text-sm">{page.name}</div>
                          <div className="text-xs text-gray-500">Page ID: {page.id} {page.category && `· ${page.category}`}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Warning about App ID vs Page ID */}
                <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                  <p className="text-xs text-red-700">
                    <strong>Important:</strong> The <strong>Page ID</strong> is NOT your App ID. Your App ID is <code>968688589058989</code>. 
                    The Page ID is the ID of your Facebook Business Page (e.g., "Premier Auto Store"). Use "Fetch My Pages" above to auto-detect it.
                  </p>
                </div>

                {/* Config Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page Access Token *</label>
                    <input
                      type="password"
                      value={formData.page_access_token}
                      onChange={e => setFormData(p => ({ ...p, page_access_token: e.target.value }))}
                      placeholder="EAAxxxxxxx..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="page-access-token-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Verify Token *</label>
                    <input
                      type="text"
                      value={formData.verify_token}
                      onChange={e => setFormData(p => ({ ...p, verify_token: e.target.value }))}
                      placeholder="any-secret-string-you-choose"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="verify-token-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Company *</label>
                    <select
                      value={formData.company_id}
                      onChange={e => setFormData(p => ({ ...p, company_id: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="company-select"
                    >
                      <option value="">Select Company</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                      {/* Fallback if user has company_id in their JWT */}
                      {companies.length === 0 && user?.company_id && (
                        <option value={user.company_id}>{user.company_name || 'My Company'} ({user.company_id.slice(0,8)}...)</option>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page ID (auto-detected)</label>
                    <input
                      type="text"
                      value={formData.page_id}
                      onChange={e => setFormData(p => ({ ...p, page_id: e.target.value }))}
                      placeholder="Auto-detected from token"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="page-id-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Brand</label>
                    <input
                      type="text"
                      value={formData.default_brand}
                      onChange={e => setFormData(p => ({ ...p, default_brand: e.target.value }))}
                      placeholder="e.g., Hyundai"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Model</label>
                    <input
                      type="text"
                      value={formData.default_model}
                      onChange={e => setFormData(p => ({ ...p, default_model: e.target.value }))}
                      placeholder="e.g., Creta"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Sales Rep</label>
                    <input
                      type="text"
                      value={formData.default_assigned_to}
                      onChange={e => setFormData(p => ({ ...p, default_assigned_to: e.target.value }))}
                      placeholder="e.g., Guna"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>
                </div>

                {/* Skip Validation Option */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <input
                    type="checkbox"
                    id="skip_validation"
                    checked={formData.skip_validation}
                    onChange={e => setFormData(p => ({ ...p, skip_validation: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    data-testid="skip-validation-checkbox"
                  />
                  <label htmlFor="skip_validation" className="text-sm text-gray-600">
                    <span className="font-medium text-gray-700">Skip token validation</span> — Use if your Page Access Token has already been verified with Facebook. Saves config without calling Graph API.
                  </label>
                </div>

                <button
                  onClick={handleSave}
                  disabled={saving || !formData.page_access_token || !formData.verify_token || !formData.company_id}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  data-testid="save-config-btn"
                >
                  {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Validating & Saving...</> : <><Send className="w-4 h-4" /> Connect & Save</>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Test Panel */}
        {showTestPanel && (
          <div className="bg-white rounded-2xl shadow-sm border border-purple-200 overflow-hidden">
            <div className="bg-purple-50 px-6 py-3 border-b border-purple-200 flex items-center justify-between">
              <h2 className="font-semibold text-purple-800 flex items-center gap-2"><TestTube className="w-4 h-4" /> Test & Simulate Leads</h2>
              <button onClick={() => setShowTestPanel(false)} className="text-purple-400 hover:text-purple-600"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* Facebook Console Test Guide */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="font-semibold text-blue-800 text-sm mb-2 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Test from Facebook Console
                </h3>
                <p className="text-xs text-blue-700 mb-2">
                  The most reliable way to test if Facebook is sending webhooks to your app:
                </p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Facebook Developer Console</a> → Your App → Webhooks</li>
                  <li>Find the <strong>"leadgen"</strong> field row</li>
                  <li>Click the <strong>"Test"</strong> link in the same row</li>
                  <li>Facebook will send a test payload to your webhook URL</li>
                  <li>Come back here and click <strong>"View Webhook Logs"</strong> to see if it arrived</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2 font-medium">If the test payload doesn't appear in logs, there may be a network/firewall issue between Facebook and your server.</p>
              </div>

              {/* Simulate Webhook (bypasses Graph API) */}
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <h3 className="font-semibold text-green-800 text-sm mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Simulate Real Lead (Recommended)
                </h3>
                <p className="text-xs text-green-700 mb-3">
                  Creates a lead exactly as it would appear from a real Meta Ad — tests the full pipeline without needing Facebook. The lead will appear in your Leads list and trigger a notification.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input type="text" value={testData.first_name} onChange={e => setTestData(p => ({ ...p, first_name: e.target.value }))} placeholder="First Name" className="px-3 py-2 border border-green-300 rounded-lg text-sm" data-testid="sim-first-name" />
                  <input type="text" value={testData.mobile} onChange={e => setTestData(p => ({ ...p, mobile: e.target.value }))} placeholder="Mobile * (10 digits)" className="px-3 py-2 border border-green-300 rounded-lg text-sm" data-testid="sim-mobile-input" />
                  <input type="text" value={testData.email} onChange={e => setTestData(p => ({ ...p, email: e.target.value }))} placeholder="Email (optional)" className="px-3 py-2 border border-green-300 rounded-lg text-sm" data-testid="sim-email-input" />
                </div>
                <button onClick={handleSimulateWebhook} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 flex items-center gap-2" data-testid="simulate-webhook-btn">
                  <Send className="w-4 h-4" /> Simulate Lead from Meta Ads
                </button>
              </div>

              {/* Quick Test (original) */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Quick Test Lead (Simple)</h3>
                <p className="text-xs text-gray-500 mb-3">Creates a basic test lead (uses your current session, not the Meta pipeline).</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input type="text" value={testData.first_name} onChange={e => setTestData(p => ({ ...p, first_name: e.target.value }))} placeholder="First Name" className="px-3 py-2 border rounded-lg text-sm" />
                  <input type="text" value={testData.mobile} onChange={e => setTestData(p => ({ ...p, mobile: e.target.value }))} placeholder="Mobile *" className="px-3 py-2 border rounded-lg text-sm" data-testid="test-mobile-input" />
                  <input type="text" value={testData.email} onChange={e => setTestData(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="px-3 py-2 border rounded-lg text-sm" />
                </div>
                <button onClick={handleTestLead} className="px-4 py-2 bg-gray-600 text-white rounded-xl text-sm font-medium hover:bg-gray-700" data-testid="send-test-btn">
                  <Send className="w-4 h-4 inline mr-1" /> Create Quick Test Lead
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sync Missed Leads Panel */}
        {showSyncPanel && (
          <div className="bg-white rounded-2xl shadow-sm border border-teal-200 overflow-hidden">
            <div className="bg-teal-50 px-6 py-3 border-b border-teal-200 flex items-center justify-between">
              <h2 className="font-semibold text-teal-800 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Sync Missed Leads from Facebook</h2>
              <button onClick={() => setShowSyncPanel(false)} className="text-teal-400 hover:text-teal-600"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">

              {/* Option 1: CSV Upload (Recommended) */}
              <div className="bg-green-50 rounded-xl p-4 border-2 border-green-300">
                <h3 className="font-semibold text-green-800 text-sm mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Option 1: Upload CSV from Facebook (Recommended)
                </h3>
                <ol className="text-xs text-green-700 space-y-1 list-decimal list-inside mb-3">
                  <li>Go to <a href="https://business.facebook.com/latest/leads_center/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Facebook Lead Ads Forms</a> (or Ads Manager → Instant Forms)</li>
                  <li>Select your form → Click the <strong>"Download"</strong> button</li>
                  <li>Choose date range → Download as <strong>CSV</strong></li>
                  <li>Upload the CSV file below</li>
                </ol>
                <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 cursor-pointer" data-testid="csv-upload-btn">
                  {uploadingCsv ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Importing...</>
                  ) : (
                    <><Zap className="w-4 h-4" /> Upload Facebook CSV</>
                  )}
                  <input type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvUpload} className="hidden" disabled={uploadingCsv} />
                </label>
              </div>

              {/* Option 2: Form ID sync (requires pages_manage_ads) */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Option 2: Sync by Form ID (requires pages_manage_ads permission)</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={syncFormId}
                    onChange={e => setSyncFormId(e.target.value)}
                    placeholder="Enter Facebook Form ID (e.g., 1234567890)"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                    data-testid="sync-form-id-input"
                  />
                  <button
                    onClick={handleSyncLeads}
                    disabled={syncing}
                    className="px-5 py-2 bg-gray-600 text-white rounded-xl text-sm font-medium hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                    data-testid="sync-leads-submit-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync'}
                  </button>
                </div>
              </div>

              {/* Results */}
              {syncResult && (
                <div className={`p-3 rounded-xl border text-sm ${syncResult.synced > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <p className="font-medium">{syncResult.message}</p>
                  {syncResult.leads?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {syncResult.leads.map((l, i) => (
                        <li key={i} className="text-xs text-green-700">#{l.lead_no}: {l.name} — {l.mobile}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Webhook Logs */}
        {showLogs && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Webhook Activity Logs</h2>
              <button onClick={() => setShowLogs(false)} className="text-gray-400 hover:text-gray-600"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No webhook events received yet. Leads from Meta ads will appear here.</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border text-sm ${log.type === 'raw_incoming' ? 'bg-blue-50 border-blue-100' : log.type === 'simulation' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-xs">{new Date(log.timestamp).toLocaleString()}</span>
                          {log.type === 'raw_incoming' && <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded font-medium">FB Payload</span>}
                          {log.type === 'simulation' && <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded font-medium">Simulated</span>}
                          {log.type === 'processed' && <span className="text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-medium">Processed</span>}
                        </div>
                        <span className="text-gray-400 text-xs">{log.entry_count} entries</span>
                      </div>
                      {log.raw_payload && (
                        <details className="mt-1">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:underline">View raw payload</summary>
                          <pre className="text-xs text-gray-600 bg-white p-2 rounded mt-1 max-h-32 overflow-y-auto break-all whitespace-pre-wrap">{log.raw_payload}</pre>
                        </details>
                      )}
                      {log.results?.map((r, i) => (
                        <div key={i} className={`text-xs mt-1 ${r.status === 'created' ? 'text-green-700' : r.status === 'duplicate' ? 'text-yellow-700' : 'text-red-700'}`}>
                          Lead {r.leadgen_id?.slice(-8)}: <strong>{r.status}</strong> {r.lead_no ? `#${r.lead_no}` : ''} {r.reason || ''}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '1', title: 'User Sees Ad', desc: 'Facebook/Instagram reel or ad with Lead Form', color: 'blue' },
              { step: '2', title: 'Fills Form', desc: 'User submits name, phone, email via the ad form', color: 'purple' },
              { step: '3', title: 'Webhook Fires', desc: 'Meta sends the lead data to your AutoBook webhook', color: 'pink' },
              { step: '4', title: 'Lead Created', desc: 'New lead appears in your Lead Module instantly', color: 'green' },
            ].map(item => (
              <div key={item.step} className={`text-center p-4 bg-${item.color}-50 rounded-xl border border-${item.color}-200`}>
                <div className={`w-10 h-10 bg-${item.color}-500 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold`}>{item.step}</div>
                <h3 className={`font-semibold text-${item.color}-800 text-sm`}>{item.title}</h3>
                <p className={`text-${item.color}-600 text-xs mt-1`}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </LeadsLayout>
  );
}

export default MetaLeadsConfig;
